import React, { useEffect } from "react";
import { Grid, Radio, FormControlLabel } from "@material-ui/core";
import { InputText, LabelText } from "@loanbossinc/component-library";
import { bindActionCreators, compose } from "redux";
import { connect } from "react-redux";
import { capitalize } from "lodash";

import { actionCreators as drawerActionCreators } from "../lenderReportingDrawer.store";
import { actionCreators as fieldActionCreators } from "../field-management/fieldManagement.store";

import FormulaFields from "./formulaFields/FormulaFields";
import ComparisonLineItems from "./line-item-comparison/comparisonLineItems";
import MultiplePropertyValue from "./line-item-value/multiplePropertyValue";
import UsedFieldsList from "./fieldsEditValue/usedFieldsList";
import ComparisonGroups from "./fieldsEditComparison/comparisonGroups";
import { actionCreators as newLineItemActionCreators } from "./lineItemManagement.store";
import {
  PaddedWrapper,
  Container,
  RadioGroupWrapper,
  StyledToggleSelect,
  StyledDecimalLabel,
  StyledDollarLabel,
  StyledPercentLabel,
  StyledRadioGroup,
  ComparisonTypeWrapper,
  StyledToggleWrapper,
  OperationTypeWrapper
} from "./styles";

const toggleButtonOptions = [
  { name: "Add", index: 0, value: "addition" },
  { name: "Subtract", index: 1, value: "subtraction" }
];

const radioButtonsOptions = [
  { value: "value", label: "Value" },
  { value: "comparison", label: "Comparison" }
];

const toggleGreaterOptions = [
  { name: "Greater Of", index: 0, value: "greaterOf" },
  { name: "Lesser Of", index: 1, value: "lesserOf" }
];

const toggleCumulativeOptions = [
  { name: "Cumulative", index: 0, value: "cumulative" },
  { name: "Individual", index: 1, value: "individual" }
];

export const determineValueFormatting = (fieldType, value) => {
  if (fieldType === "dollars" || fieldType === "mm") {
    return (
      <StyledDollarLabel
        value={(value && Math.abs(value)) || " "}
        hasAccountingFormat={value < 0}
        decimals={2}
        style={{ color: value < 0 ? "#C40A0A" : "#000" }}
      />
    );
  } else if (fieldType === "percent") {
    return <StyledPercentLabel decimals={2} value={value} />;
  }

  return <StyledDecimalLabel decimals={2} value={value} />;
};

export const determineFieldType = field => {
  if (field.isFromLoanBoss) {
    return "Derived from LoanBoss";
  }
  if (field.isHardCoded) {
    return "Manual input";
  }
  if (field.isCalculated) {
    return "Calculated";
  }
  if (field.isFromFile) {
    return "From file";
  }
  if (field.isBalanceOverride || field.isFinanceOverride) {
    return "Cashflow override";
  }
  if (field.isFromRentRoll) {
    return "From rent roll";
  }

  return "Unknown";
};

const DefaultLabelText = ({ value }) => <LabelText value={value} style={{ fontSize: "12px", fontWeight: "normal" }} />;

const LineItemManagement = props => {
  const {
    deal,
    isOpen,
    newLineItemType,
    newLineItemName,
    setNewLineItemSelections,
    setNewLineItemsSelectionsToDefault,
    setDrawerSelections,
    newLineItemOperator,
    isMultipleProperties,
    isEditingLineItem,
    addLineItemType,
    comparisonOperator,
    comparisonCalculationType,
    accountingIntegrations
  } = props;

  useEffect(() => {
    const hasMultipleProperties = deal && deal.properties && deal.properties.length > 1;
    setNewLineItemSelections({ isMultipleProperties: hasMultipleProperties });
  }, [deal]);

  useEffect(() => {
    if (!isOpen) {
      setNewLineItemsSelectionsToDefault();
      setDrawerSelections({ isEditingLineItem: false });
    }
  }, [isOpen]);

  const handleRadioChange = e => setNewLineItemSelections({ newLineItemType: e.target.value });

  const handleNewLineName = e => setNewLineItemSelections({ newLineItemName: e.target.value });

  const handleOperatorChange = option => {
    const selected = option || toggleButtonOptions[0];
    setNewLineItemSelections({
      newLineItemOperator: selected.value
    });
  };

  const handleGreaterChange = option => {
    const selected = option || toggleGreaterOptions[0];
    setNewLineItemSelections({ comparisonOperator: selected.value });
  };

  const handleCumulativeChange = option => {
    const selected = option || toggleCumulativeOptions[0];
    setNewLineItemSelections({ comparisonCalculationType: selected.value });
  };

  const determineSelectionContent = () => {
    if (newLineItemType === "value") {
      if (isMultipleProperties) {
        return <MultiplePropertyValue properties={deal && deal.properties} {...props} />;
      }

      return <FormulaFields deal={deal} accountingIntegrations={accountingIntegrations} />;
    }

    return <ComparisonLineItems properties={deal && deal.properties} {...props} />;
  };

  const determineEditContent = () => {
    if (newLineItemType === "value") {
      return <UsedFieldsList properties={deal && deal.properties} {...props} />;
    }

    return <ComparisonGroups {...props} properties={deal && deal.properties} />;
  };

  return (
    <>
      <Container>
        <Grid container justify="space-between" alignItems="center">
          <Grid item xs={9}>
            <PaddedWrapper>
              <InputText
                value={newLineItemName}
                variant="filled"
                id="line-item-name"
                name="item-name"
                label="Line item name"
                fullWidth
                onChange={handleNewLineName}
              />
            </PaddedWrapper>
          </Grid>
        </Grid>
        {!isEditingLineItem && (
          <RadioGroupWrapper>
            <PaddedWrapper>
              <DefaultLabelText value="Line Item Type" />
            </PaddedWrapper>
            <PaddedWrapper>
              <StyledRadioGroup value={newLineItemType} aria-label="value-or-comparison" name="lineItemType" onChange={handleRadioChange}>
                {radioButtonsOptions.map(option => (
                  <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={<LabelText value={option.label} />} />
                ))}
              </StyledRadioGroup>
            </PaddedWrapper>
          </RadioGroupWrapper>
        )}
        {newLineItemType === "comparison" && (
          <ComparisonTypeWrapper>
            <StyledToggleWrapper mr="3em">
              <DefaultLabelText value="Comparison" />
              <StyledToggleSelect options={toggleGreaterOptions} onChange={handleGreaterChange} defaultIndex={comparisonOperator === "greaterOf" ? 0 : 1} />
            </StyledToggleWrapper>
            {isMultipleProperties && (
              <StyledToggleWrapper>
                <DefaultLabelText value="Calculation" />
                <StyledToggleSelect
                  options={toggleCumulativeOptions}
                  onChange={handleCumulativeChange}
                  defaultIndex={comparisonCalculationType === "cumulative" ? 0 : 1}
                />
              </StyledToggleWrapper>
            )}
          </ComparisonTypeWrapper>
        )}
        <OperationTypeWrapper>
          <div style={{ width: "38%", lineHeight: 1 }}>
            <DefaultLabelText
              value={`Select whether you’d like this line item to add to or subtract from the total of Adjusted ${capitalize(addLineItemType)}`}
            />
          </div>
          <StyledToggleSelect options={toggleButtonOptions} onChange={handleOperatorChange} defaultIndex={newLineItemOperator === "addition" ? 0 : 1} />
        </OperationTypeWrapper>
      </Container>
      {!isEditingLineItem ? determineSelectionContent() : determineEditContent()}
    </>
  );
};

const mapStateToProps = ({
  lenderReportingDrawer,
  lenderReportNewLineItemManagement,
  lenderReportFieldManagement: { fields },
  lenderReportingNoiCalculation: { addLineItemType }
}) => ({
  ...lenderReportNewLineItemManagement,
  ...lenderReportingDrawer,
  addLineItemType,
  fields
});

const mapDispatchToProps = dispatch => bindActionCreators({ ...drawerActionCreators, ...newLineItemActionCreators, ...fieldActionCreators }, dispatch);

export default compose(connect(mapStateToProps, mapDispatchToProps))(LineItemManagement);
